import { useCallback, useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/** True when the error looks like an HTTP 5xx response from the catalog service. */
const isCatalog5xx = (error: unknown): boolean => {
  if (error && typeof error === 'object') {
    const status =
      (error as any).response?.status ?? (error as any).status ?? null;
    return typeof status === 'number' && status >= 500 && status <= 504;
  }
  return false;
};

export interface DeferEligibilityResult {
  /** null = check in progress, true = eligible, false = not eligible */
  eligible: boolean | null;
  /** true when catalog returned a 5xx and eligibility could not be determined */
  catalogError: boolean;
  /** call to re-run the catalog eligibility check */
  retry: () => void;
}

/**
 * Checks whether the current user is eligible to defer a budget action for the
 * given entity. Eligibility means the user is a member of the group that owns
 * the entity, according to the Backstage catalog.
 *
 * The check only runs for `checkId === 'budget-compliance-check'`.
 */
export const useDeferEligibility = (
  entityRef: string | undefined,
  checkId: string | undefined,
): DeferEligibilityResult => {
  const [eligible, setEligible] = useState<boolean | null>(null);
  const [catalogError, setCatalogError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  const retry = useCallback(() => {
    setEligible(null);
    setCatalogError(false);
    setRetryCount(c => c + 1);
  }, []);

  useEffect(() => {
    if (checkId !== 'budget-compliance-check' || !entityRef) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const { email } = await identityApi.getProfileInfo();
        const userFormattedEmail = email?.replace('@', '_').toLowerCase() ?? '';

        const entity = await catalogApi.getEntityByRef(entityRef);
        if (!entity) {
          if (!cancelled) setEligible(false);
          return;
        }

        const ownerRef = entity.spec?.owner as string | undefined;
        if (!ownerRef) {
          if (!cancelled) setEligible(false);
          return;
        }

        const ownerEntity = await catalogApi.getEntityByRef(ownerRef);
        if (!ownerEntity) {
          if (!cancelled) setEligible(false);
          return;
        }

        // Collect members from spec.members (user names) and hasMember relations
        const specMembers: string[] = Array.isArray(ownerEntity.spec?.members)
          ? (ownerEntity.spec.members as string[]).map(m =>
              (m.includes('/') ? m.split('/')[1] : m).toLowerCase(),
            )
          : [];

        const relationMembers: string[] = (ownerEntity.relations ?? [])
          .filter(r => r.type === 'hasMember')
          .map(r => {
            const ref = r.targetRef;
            return (ref.includes('/') ? ref.split('/')[1] : ref).toLowerCase();
          });

        const allMembers = new Set([...specMembers, ...relationMembers]);

        if (!cancelled) {
          setEligible(allMembers.has(userFormattedEmail));
        }
      } catch (error) {
        if (!cancelled) {
          if (isCatalog5xx(error)) {
            setCatalogError(true);
          } else {
            setEligible(false);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // retryCount intentionally included so retry() re-runs the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityRef, checkId, catalogApi, identityApi, retryCount]);

  return { eligible, catalogError, retry };
};
